import HeadingComponent from "@/components/BoardPack/components/shared/heading";
import { TableComponent, transformApiResponse } from "@/components/BoardPack/pages/large_table";
import ErrorState from "@/components/common/ErrorStateComponent";
import LoadingComponent from "@/components/common/LoadingComponent";
import { useEffect, useRef, useState } from "react";

function LargeTable({
  year = "",
  month = "",
  monthNumber = "",
  contract = "",
  page = null,
  isExporting = false,
  cfoComment = "",
  onCfoCommentChange,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tableData, setTableData] = useState({ columns: [], rows: [] });

  const hasComment = cfoComment.trim() !== "";


  useEffect(() => {
    fetchData();
  }, []);

  const is_cfo = page?.params?.is_cfo ? true : false;
  const title = page?.title;
  const subtitle = page?.subtitle;
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      setSummary(null);
      const response =  mockApiResponse; 
      if (!response?.success) throw new Error(response?.message || "API error");
      setSummary({ lines: response.lines || [] });
      const { columns, rows } = transformApiResponse(response);
      setTableData({ columns, rows });
    } catch (err) {
      console.error("Profit & Loss fetch error:", err);
      setError(err.message || "Failed to load data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchData} />;
  }
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        marginTop: "12px",
      }}
    >
      <div className="flex flex-col gap-1 mb-1 mt-3">
        {title && <HeadingComponent variant={"main"}>{title}</HeadingComponent>}
        {subtitle && <HeadingComponent variant={"sub"}>{subtitle}</HeadingComponent>}
      </div>
      <div style={{  zoom: "80%" }} className={`px-[16px] ${!is_cfo && "pb-[100px]"}`}>
        <TableComponent
          // title={page?.title || "Profit and Loss - Current Month / YTD / Full Year"}
          title={"Profit and Loss - Current Month / YTD / Full Year"}
          columns={tableData.columns}
          rows={tableData.rows}
          contract={page?.subtitle}
        />
      </div>

       <>
        {is_cfo && summary?.lines.length > 0 && (
          <>
            <div className={`mt-1 flex items-start gap-4 p-4 ${is_cfo && "mb-8"}`}>
              <div className="flex-1 p-2">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-5 bg-blue-600 rounded-full"></div>
                  <h3 className="text-sm font-semibold">AI Summary</h3>
                </div>
                {summary?.lines && summary.lines.length > 0 ? (
                  <ul className="space-y-1">
                    {summary.lines.map((line, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-[12px] font-medium">
                          {line.replace(/^\s*-\s*/, "")}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-gray-500">No AI summary available</p>
                )}
              </div>
              {(!isExporting || (isExporting && hasComment)) && (
                <div className="flex-1 p-2">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1 h-5 bg-blue-600 rounded-full"></div>
                    <h3 className="text-sm font-semibold">CFO Comments</h3>
                  </div>
                  {!isExporting ? (
                    <textarea
                      value={cfoComment}
                      onChange={(e) => onCfoCommentChange?.(e.target.value)}
                      placeholder="Enter comments here..."
                      className="w-full h-32 p-3 border border-gray-300 rounded-md text-xs resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    />
                  ) : hasComment ? (
                    <p className="text-xs whitespace-pre-wrap break-words">{cfoComment}</p>
                  ) : null}
                </div>
              )}
            </div>
          </>
        )}
      </>
    </div>
  );
}

export default LargeTable;



export const mockApiResponse = {
    "success": true,
    "meta": {
        "year": 2026,
        "month": "May",
        "measure": 2
    },
    "mdx": "SELECT NON EMPTY { ([Year].[2026], [Month].[Jan], [Version].[Actual]), ([Year].[2026], [Month].[Feb], [Version].[Actual]), ([Year].[2026], [Month].[Mar], [Version].[Actual]), ([Year].[2026], [Month].[Apr], [Version].[Actual]), ([Year].[2026], [Month].[May], [Version].[Actual]), ([Year].[2026], [Month].[Jun], [Version].[Budget]), ([Year].[2026], [Month].[Jul], [Version].[Budget]), ([Year].[2026], [Month].[Aug], [Version].[Budget]), ([Year].[2026], [Month].[Sep], [Version].[Budget]), ([Year].[2026], [Month].[Oct], [Version].[Budget]), ([Year].[2026], [Month].[Nov], [Version].[Budget]), ([Year].[2026], [Month].[Dec], [Version].[Budget]), ([Year].[2026], [Month].[Dec YTD], [Version].[Actual]), ([Year].[2026], [Month].[Dec YTD], [Version].[Budget]), ([Year].[2025], [Month].[Dec YTD], [Version].[Actual]) } ON COLUMNS, { [P&L Hierarchy].[Revenue], [P&L Hierarchy].[DAILY PARKING], [P&L Hierarchy].[Season Tickets], [P&L Hierarchy].[Fines & Penalties], [P&L Hierarchy].[Clamp & Removal], [P&L Hierarchy].[POUND], [P&L Hierarchy].[OTHER INCOME], [P&L Hierarchy].[CASH EXCESS / (SHORT)], [P&L Hierarchy].[Validations], [P&L Hierarchy].[Refunds], [P&L Hierarchy].[WRAPPING BUSINESS], [P&L Hierarchy].[Total Revenue], [P&L Hierarchy].[Total Rent], [P&L Hierarchy].[Rent V Revenue %], [P&L Hierarchy].[Staff Cost-Direct %], [P&L Hierarchy].[701101], [P&L Hierarchy].[Total Staff Cost - Direct], [P&L Hierarchy].[Total Other Direct], [P&L Hierarchy].[EBITDA], [P&L Hierarchy].[EBITDA Margin], [P&L Hierarchy].[Total Depreciation & Amortization], [P&L Hierarchy].[Zakat, Exceptional & CG], [P&L Hierarchy].[OPERATING PROFIT/(LOSS)], [P&L Hierarchy].[NET PROFIT MARGIN] } ON ROWS FROM [Profit & Loss] WHERE ( [Display].[Local Currency], [Dim3].[All], [Currency].[All Currency], [Cost Center].[Total Cost Center], [Contract].[2051001] )",
    "data": {
        "headerYears": [
            "2026",
            "2026",
            "2026",
            "2026",
            "2026",
            "2026",
            "2026",
            "2026",
            "2026",
            "2026",
            "2026",
            "2026",
            "2026",
            "2026",
            "V Budget",
            "V Budget",
            "2026",
            "2025",
            "2026 V 2025",
            "2026 V 2025"
        ],
        "headerMonths": [
            "Actual",
            "Actual",
            "Actual",
            "Actual",
            "Actual",
            "Budget",
            "Budget",
            "Budget",
            "Budget",
            "Budget",
            "Budget",
            "Budget",
            "Outturn",
            "Budget",
            "Variance",
            "% Variance",
            "Outturn",
            "Actual",
            "Variance",
            "% Variance"
        ],
        "headerThird": [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
            "FY Outturn",
            "Dec YTD",
            "Dec YTD",
            "Dec YTD",
            "FY Outturn",
            "Dec YTD",
            "Dec YTD",
            "Dec YTD"
        ],
        "rows": [
            {
                "label": "Revenue",
                "values": [
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-"
                ],
                "isPercent": false
            },
            {
                "label": "DAILY PARKING",
                "values": [
                    "4,243,405",
                    "3,802,184",
                    "1,490,028",
                    "2,101,753",
                    "-",
                    "3,368,426",
                    "3,755,092",
                    "4,132,587",
                    "3,621,809",
                    "3,881,463",
                    "3,423,638",
                    "3,906,170",
                    "37,726,556",
                    "42,706,820",
                    "(4,980,265)",
                    "(11.66%)",
                    "37,726,556",
                    "43,396,917",
                    "(5,670,361)",
                    "(13.07%)"
                ],
                "isPercent": false
            },
            {
                "label": "Season Tickets",
                "values": [
                    "36,128",
                    "39,150",
                    "37,625",
                    "39,128",
                    "-",
                    "40,000",
                    "40,000",
                    "40,000",
                    "40,000",
                    "40,000",
                    "40,000",
                    "40,000",
                    "432,030",
                    "480,000",
                    "(47,970)",
                    "(9.99%)",
                    "432,030",
                    "476,432",
                    "(44,401)",
                    "(9.32%)"
                ],
                "isPercent": false
            },
            {
                "label": "Fines & Penalties",
                "values": [
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-"
                ],
                "isPercent": false
            },
            {
                "label": "Clamp & Removal",
                "values": [
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-"
                ],
                "isPercent": false
            },
            {
                "label": "POUND",
                "values": [
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-"
                ],
                "isPercent": false
            },
            {
                "label": "OTHER INCOME",
                "values": [
                    "7,083",
                    "7,083",
                    "7,083",
                    "7,083",
                    "-",
                    "7,083",
                    "7,083",
                    "7,083",
                    "7,083",
                    "7,083",
                    "7,083",
                    "7,083",
                    "77,915",
                    "84,998",
                    "(7,083)",
                    "(8.33%)",
                    "77,915",
                    "85,000",
                    "(7,085)",
                    "(8.34%)"
                ],
                "isPercent": false
            },
            {
                "label": "CASH EXCESS / (SHORT)",
                "values": [
                    "(130)",
                    "(472)",
                    "(71)",
                    "(71)",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "(745)",
                    "-",
                    "-",
                    "-",
                    "(745)",
                    "-",
                    "-",
                    "-"
                ],
                "isPercent": false
            },
            {
                "label": "Validations",
                "values": [
                    "64,818",
                    "52,607",
                    "22,697",
                    "21,104",
                    "-",
                    "44,000",
                    "44,000",
                    "44,000",
                    "44,000",
                    "44,000",
                    "44,000",
                    "44,000",
                    "469,227",
                    "493,000",
                    "(23,773)",
                    "(4.82%)",
                    "469,227",
                    "494,084",
                    "(24,857)",
                    "(5.03%)"
                ],
                "isPercent": false
            },
            {
                "label": "Refunds",
                "values": [
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-"
                ],
                "isPercent": false
            },
            {
                "label": "WRAPPING BUSINESS",
                "values": [
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-",
                    "-"
                ],
                "isPercent": false
            },
            {
                "label": "Total Revenue",
                "values": [
                    "4,351,304",
                    "3,900,552",
                    "1,557,362",
                    "2,168,997",
                    "-",
                    "3,459,509",
                    "3,846,176",
                    "4,223,670",
                    "3,712,892",
                    "3,972,546",
                    "3,514,721",
                    "3,997,253",
                    "38,704,983",
                    "43,764,819",
                    "(5,059,836)",
                    "(11.56%)",
                    "38,704,983",
                    "44,452,432",
                    "(5,747,450)",
                    "(12.93%)"
                ],
                "isPercent": false
            },
            {
                "label": "Total Rent",
                "values": [
                    "(2,719,565)",
                    "(2,437,845)",
                    "(973,351)",
                    "(1,355,623)",
                    "(1,738,911)",
                    "(2,075,705)",
                    "(2,307,705)",
                    "(2,534,202)",
                    "(2,227,735)",
                    "(2,383,528)",
                    "(2,108,833)",
                    "(2,398,352)",
                    "(25,261,356)",
                    "(26,258,891)",
                    "997,535",
                    "3.80%",
                    "(25,261,356)",
                    "(26,671,459)",
                    "1,410,103",
                    "5.29%"
                ],
                "isPercent": false
            },
            {
                "label": "Rent V Revenue %",
                "values": [
                    "62.50%",
                    "62.50%",
                    "62.50%",
                    "62.50%",
                    "-",
                    "60.00%",
                    "60.00%",
                    "60.00%",
                    "60.00%",
                    "60.00%",
                    "60.00%",
                    "60.00%",
                    "65.27%",
                    "60.00%",
                    "-",
                    "(5.27%)",
                    "65.27%",
                    "60.00%",
                    "-",
                    "(5.27%)"
                ],
                "isPercent": true
            },
            {
                "label": "Staff Cost-Direct %",
                "values": [
                    "1.35%",
                    "1.50%",
                    "4.21%",
                    "2.94%",
                    "-",
                    "1.70%",
                    "1.53%",
                    "1.39%",
                    "1.58%",
                    "1.48%",
                    "1.67%",
                    "1.47%",
                    "1.70%",
                    "1.61%",
                    "-",
                    "(0.09%)",
                    "1.70%",
                    "1.58%",
                    "-",
                    "(0.12%)"
                ],
                "isPercent": true
            },
            {
                "label": "Total Staff Cost - Direct",
                "values": [
                    "(208,442)",
                    "(159,123)",
                    "(174,518)",
                    "(150,749)",
                    "(864)",
                    "(176,025)",
                    "(179,730)",
                    "(183,347)",
                    "(178,453)",
                    "(180,941)",
                    "(176,553)",
                    "(181,178)",
                    "(1,949,923)",
                    "(2,133,865)",
                    "183,943",
                    "8.62%",
                    "(1,949,923)",
                    "(2,280,184)",
                    "330,261",
                    "14.48%"
                ],
                "isPercent": false
            },
            {
                "label": "Total Other Direct",
                "values": [
                    "(185,973)",
                    "(167,294)",
                    "(135,982)",
                    "(145,542)",
                    "(1,293)",
                    "(131,044)",
                    "(136,059)",
                    "(146,530)",
                    "(139,281)",
                    "(142,966)",
                    "(136,469)",
                    "(143,317)",
                    "(1,611,750)",
                    "(1,644,581)",
                    "32,831",
                    "2%",
                    "(1,611,750)",
                    "(1,912,095)",
                    "300,345",
                    "15.71%"
                ],
                "isPercent": false
            },
            {
                "label": "EBITDA",
                "values": [
                    "1,237,324",
                    "1,136,291",
                    "273,510",
                    "517,083",
                    "(1,741,068)",
                    "1,076,735",
                    "1,222,681",
                    "1,359,591",
                    "1,167,423",
                    "1,265,112",
                    "1,092,866",
                    "1,274,407",
                    "9,881,954",
                    "13,727,481",
                    "(3,845,527)",
                    "(28.01%)",
                    "9,881,954",
                    "13,588,694",
                    "(3,706,740)",
                    "(27.28%)"
                ],
                "isPercent": false
            },
            {
                "label": "EBITDA Margin",
                "values": [
                    "28.44%",
                    "29.13%",
                    "17.56%",
                    "23.84%",
                    "-",
                    "31.12%",
                    "31.79%",
                    "32.19%",
                    "31.44%",
                    "31.85%",
                    "31.09%",
                    "31.88%",
                    "25.53%",
                    "31.37%",
                    "-",
                    "(5.83%)",
                    "25.53%",
                    "30.57%",
                    "-",
                    "(5.04%)"
                ],
                "isPercent": true
            },
            {
                "label": "Total Depreciation & Amortization",
                "values": [
                    "(196,346)",
                    "(196,447)",
                    "(196,478)",
                    "(199,082)",
                    "-",
                    "(197,440)",
                    "(197,440)",
                    "(197,440)",
                    "(197,440)",
                    "(197,440)",
                    "(197,440)",
                    "(197,440)",
                    "(2,170,431)",
                    "(2,369,277)",
                    "198,846",
                    "8.39%",
                    "(2,170,431)",
                    "(2,561,451)",
                    "391,020",
                    "15.27%"
                ],
                "isPercent": false
            },
            {
                "label": "Zakat, Exceptional & CG",
                "values": [
                    "(324,576)",
                    "(295,028)",
                    "(177,868)",
                    "(208,450)",
                    "-",
                    "(242,166)",
                    "(269,232)",
                    "(295,657)",
                    "(259,902)",
                    "(278,078)",
                    "(246,031)",
                    "(279,808)",
                    "(2,876,796)",
                    "(3,063,537)",
                    "186,742",
                    "6.10%",
                    "(2,876,796)",
                    "(3,690,347)",
                    "813,551",
                    "22.05%"
                ],
                "isPercent": false
            },
            {
                "label": "OPERATING PROFIT/(LOSS)",
                "values": [
                    "716,402",
                    "644,816",
                    "(100,836)",
                    "109,551",
                    "(1,741,068)",
                    "637,130",
                    "756,009",
                    "866,494",
                    "710,081",
                    "789,594",
                    "649,396",
                    "797,159",
                    "4,834,728",
                    "8,294,667",
                    "(3,459,940)",
                    "(41.71%)",
                    "4,834,728",
                    "7,336,896",
                    "(2,502,168)",
                    "(34.10%)"
                ],
                "isPercent": false
            },
            {
                "label": "NET PROFIT MARGIN",
                "values": [
                    "16.46%",
                    "16.53%",
                    "(6.47%)",
                    "5.05%",
                    "-",
                    "18.42%",
                    "19.66%",
                    "20.52%",
                    "19.12%",
                    "19.88%",
                    "18.48%",
                    "19.94%",
                    "12.49%",
                    "18.95%",
                    "-",
                    "(6.46%)",
                    "12.49%",
                    "16.51%",
                    "-",
                    "(4.01%)"
                ],
                "isPercent": true
            }
        ]
    },
    "is_cfo": true,
    "is_ai": true,
    "ai_prompt": "You are a CFO analyst. Write EXACTLY 6 bullet points summarizing this Full Year Outturn vs Budget data.\n\nContract: Sharjah Airport | Year: 2026 | Month: May\n\nDATA:\nDAILY PARKING: FY_Outturn=37.7M, FY_Budget=-, vs_Budget=down 11.66%\nSeason Tickets: FY_Outturn=432K, FY_Budget=-, vs_Budget=down 9.99%\nOTHER INCOME: FY_Outturn=77.9K, FY_Budget=-, vs_Budget=down 8.33%\nCASH EXCESS / (SHORT): FY_Outturn=(745), FY_Budget=-, vs_Budget=no data\nValidations: FY_Outturn=469.2K, FY_Budget=-, vs_Budget=down 4.82%\nTotal Revenue: FY_Outturn=38.7M, FY_Budget=-, vs_Budget=down 11.56%\nTotal Rent: FY_Outturn=-25.3M, FY_Budget=-, vs_Budget=up 3.80%\nRent V Revenue %: FY_Outturn=65.27%, FY_Budget=-, vs_Budget=down 5.27%\nStaff Cost-Direct %: FY_Outturn=1.70%, FY_Budget=-, vs_Budget=down 0.09%\nTotal Staff Cost - Direct: FY_Outturn=-1.9M, FY_Budget=-, vs_Budget=up 8.62%\nTotal Other Direct: FY_Outturn=-1.6M, FY_Budget=-, vs_Budget=up 2%\nEBITDA: FY_Outturn=9.9M, FY_Budget=-, vs_Budget=down 28.01%\nEBITDA Margin: FY_Outturn=25.53%, FY_Budget=-, vs_Budget=down 5.83%\nTotal Depreciation & Amortization: FY_Outturn=-2.2M, FY_Budget=-, vs_Budget=up 8.39%\nZakat, Exceptional & CG: FY_Outturn=-2.9M, FY_Budget=-, vs_Budget=up 6.10%\nOPERATING PROFIT/(LOSS): FY_Outturn=4.8M, FY_Budget=-, vs_Budget=down 41.71%\nNET PROFIT MARGIN: FY_Outturn=12.49%, FY_Budget=-, vs_Budget=down 6.46%\n\nIMPORTANT: The DATA above already contains pre-calculated variance directions.\n- \"up X%\" means performance INCREASED by X%\n- \"down X%\" means performance DECREASED by X%\n- \"no change\" means zero variance\n- \"no data\" means comparison not available\nCopy these directions EXACTLY as given — do NOT reverse or recalculate them.\n\nSTRICT RULES:\n- Output EXACTLY 6 lines. Not more, not less.\n- First bullet MUST mention Sharjah Airport, May, 2026.\n- Each line MUST start with \"- \" (dash space)\n- Each bullet must be 15-20 words — detailed, mention actual value AND variance\n- Use the exact up/down direction from DATA — never guess or reverse it\n- No currency symbols (no USD, SAR, $)\n- No titles, no explanations, no blank lines — ONLY bullet lines\n\nFOLLOW THIS FORMAT (replace placeholders with real DATA values):\n- Sharjah Airport Full Year Outturn vs Budget summary for 2026.\n- Total Revenue was [FY_Outturn], [above/below] budget of [FY_Budget] by [X]%.\n- EBITDA stood at [FY_Outturn], [ahead/behind] budget of [FY_Budget] by [X]%.\n- EBITDA Margin was [X]%, [above/below] budgeted margin of [Y]%.\n- Operating Profit was [FY_Outturn], [above/below] budget by [X]%.\n- Net Profit Margin at [X]%, [above/below] budgeted [Y]%.\n- Overall FY performance [summary statement vs budget].\n\nYOUR OUTPUT:",
    "ai_summary": "- Sharjah Airport operational summary for May 2026.\n- DAILY PARKING was 37.7M, down 11.66% vs comparison.\n- Season Tickets was 432K, down 9.99% vs comparison.\n- OTHER INCOME was 77.9K, down 8.33% vs comparison.\n- CASH EXCESS / (SHORT) was (745).\n- Validations was 469.2K, down 4.82% vs comparison.",
    "lines": [
        "- Sharjah Airport operational summary for May 2026.",
        "- DAILY PARKING was 37.7M, down 11.66% vs comparison.",
        "- Season Tickets was 432K, down 9.99% vs comparison.",
        "- OTHER INCOME was 77.9K, down 8.33% vs comparison.",
        "- CASH EXCESS / (SHORT) was (745).",
        "- Validations was 469.2K, down 4.82% vs comparison."
    ]
}